import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Button } from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { Host } from '@/api/bol';
import { PageLayout, DataGridContainer, JsonDetailPanel } from '@/components';
import { useDataGridSelection, usePagination, useSnackbarNotification, usePermissions, getActionTooltip } from '@/hooks';
import { createHostColumns } from '@/components/bol/management/hosts/HostColumns';
import { HostFormDialog } from '@/components/bol/management/hosts/HostFormDialog';
import { HostDeleteDialog } from '@/components/bol/management/hosts/HostDeleteDialog';
import { useHosts } from '@/hooks/useHosts';

const HostsPage: React.FC = () => {
  const { can } = usePermissions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Use custom hooks for host management
  const {
    hosts,
    loading,
    selectedHost,
    setSelectedHost,
    fetchHosts,
    createHost,
    deleteHost,
    total
  } = useHosts();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedHostForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<Host>({ 
    data: hosts, 
    getItemId: (host) => host.name 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchHosts(page, pageSize);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarType,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleCreateHost = async (hostData: { name: string; uri: string }) => {
    await createHost(hostData);
    setCreateDialogOpen(false);
    fetchHosts(pagination.page, pagination.pageSize);
  };

  const handleDeleteHost = (host: Host) => {
    setSelectedHost(host);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (hostName: string) => {
    await deleteHost(hostName);
    setSelectedHost(null);
    setDeleteDialogOpen(false);
    fetchHosts(pagination.page, pagination.pageSize);
  };

  const handleRefresh = () => {
    fetchHosts(pagination.page, pagination.pageSize);
  };

  // Create columns configuration
  const columns = createHostColumns({
    canEdit: false, // Edit not available yet
    canDelete: can.deleteHost,
    onDelete: handleDeleteHost,
    deleteTooltip: getActionTooltip('deleteHost')
  });  useEffect(() => {
    fetchHosts();
  }, [fetchHosts]);

  return (
    <PageLayout 
      title="Hosts Management"
      selectedItemInfo={selectedHostForDetail ? `Selected: ${selectedHostForDetail.name}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <Tooltip title="Refresh hosts list">
          <IconButton
            onClick={handleRefresh}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Tooltip title={!can.createHost ? getActionTooltip('createHost') : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<Add />}
              disabled={!can.createHost}
            >
              Create Host
            </Button>
          </span>
        </Tooltip>
      }
      sidePanel={selectedHostForDetail ? (
        <JsonDetailPanel
          title="Host Details"
          data={selectedHostForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={hosts}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getRowId={(row: { name: any; }) => row.name}
          columnOrderStorageKey="hosts-columns"
        />
      </Box>

      {/* Create Host Dialog */}
      <HostFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateHost}
      />

      {/* Delete Confirmation Dialog */}
      <HostDeleteDialog
        open={deleteDialogOpen}
        host={selectedHost}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedHost(null);
        }}
        onDelete={handleConfirmDelete}
      />
    </PageLayout>
  );
};

export default HostsPage;
